import { isStudentProgram, type StudentProgram } from './studentPrograms'

/**
 * The axes the students list can be narrowed along.
 *
 * All of them are answered by the server rather than by a pass over the loaded
 * rows, because the roster is paged: a client-side filter answers "who is in
 * Grade 9" with "whoever is in Grade 9 among the first fifty children", and
 * answers it without ever looking incomplete. `useStudentsInfinite` keys its
 * pages on this object for the same reason — each narrowing is its own list, not
 * a view of one.
 *
 * `null` means "not asked" on every axis. The one value that is *not* a null is
 * `homeroomId: 'none'`, which is a real choice meaning the children in no
 * homeroom at all — and `photoConsent: 'unasked'`, likewise a real choice
 * meaning nobody has put the question to that family yet.
 */
export interface StudentFilters {
  /** A homeroom id, `'none'` for the children in no group, or null for all. */
  homeroomId: string | null
  /** In the schema's encoding: -1 is Pre-K, 0 is Kindergarten. */
  gradeLevel: number | null
  programType: StudentProgram | null
  graduationYear: number | null
  /** Three-valued, like the column: asked and yes, asked and no, never asked. */
  photoConsent: PhotoConsentFilter | null
}

export const PHOTO_CONSENT_FILTERS = ['yes', 'no', 'unasked'] as const
export type PhotoConsentFilter = (typeof PHOTO_CONSENT_FILTERS)[number]

export const PHOTO_CONSENT_FILTER_LABELS: Record<PhotoConsentFilter, string> = {
  yes: 'Consent given',
  no: 'Consent refused',
  unasked: 'Never asked',
}

/** The unfiltered roster. Frozen so a caller cannot narrow it by accident. */
export const NO_STUDENT_FILTERS: StudentFilters = Object.freeze({
  homeroomId: null,
  gradeLevel: null,
  programType: null,
  graduationYear: null,
  photoConsent: null,
})

/**
 * The URL parameter each axis is kept under.
 *
 * Short, because these end up in a link somebody pastes into an email, and
 * deliberately *not* the API's own names: `?grade=9` in the address bar is the
 * page's vocabulary, and keeping the two separate means renaming one does not
 * silently change the other.
 */
const URL_KEYS = {
  homeroomId: 'homeroom',
  gradeLevel: 'grade',
  programType: 'program',
  graduationYear: 'graduation',
  photoConsent: 'photoConsent',
} as const satisfies Record<keyof StudentFilters, string>

/**
 * The filters as `GET /api/students` takes them, ready to append.
 *
 * Leading `&` on each, and empty for an unfiltered roster, so the request the
 * page has always made stays byte-for-byte what it was. An axis that is not set
 * is left off entirely rather than sent empty: `?gradeLevel=` is what a cleared
 * control serialises to, and a route that read it as a grade would empty the
 * list in a way nothing on screen explains.
 */
export function studentFilterQuery(filters: StudentFilters): string {
  const parts: string[] = []
  const add = (name: string, value: string | number) =>
    parts.push(`&${name}=${encodeURIComponent(value)}`)

  // Compared against null rather than tested for truth, on every numeric axis:
  // grade 0 is Kindergarten and grade -1 is Pre-K, and a falsy check drops both.
  if (filters.homeroomId !== null) add('homeroomId', filters.homeroomId)
  if (filters.gradeLevel !== null) add('gradeLevel', filters.gradeLevel)
  if (filters.programType !== null) add('programType', filters.programType)
  if (filters.graduationYear !== null) add('graduationYear', filters.graduationYear)
  if (filters.photoConsent !== null) add('photoConsent', filters.photoConsent)

  return parts.join('')
}

/** How many axes are narrowed — what the bar's "Clear" affordance counts. */
export function activeStudentFilterCount(filters: StudentFilters): number {
  return Object.values(filters).filter((value) => value !== null).length
}

/**
 * The filters a URL is asking for, dropping anything the app cannot honour.
 *
 * Dropped rather than passed through, because the alternative is a 400 on an
 * ordinary page visit: a stale link, a hand-edited address or a grade the school
 * has since retired would otherwise be forwarded to the API verbatim. An
 * unfiltered roster under an empty bar is a state the page can explain; an error
 * page is not.
 */
export function parseStudentFilters(params: URLSearchParams): StudentFilters {
  return {
    homeroomId: params.get(URL_KEYS.homeroomId) || null,
    gradeLevel: parseIntegerParam(params.get(URL_KEYS.gradeLevel), -1, 12),
    programType: parseEnumParam(params.get(URL_KEYS.programType), isStudentProgram),
    graduationYear: parseIntegerParam(params.get(URL_KEYS.graduationYear), 1900, 2200),
    photoConsent: parseEnumParam(params.get(URL_KEYS.photoConsent), isPhotoConsentFilter),
  }
}

/**
 * `params` with the filter keys rewritten to match `filters`.
 *
 * Built from the params it is handed rather than from scratch: the roster's tab
 * lives in the same query string, and rebuilding would drop a reader from the
 * withdrawn list back onto the roster every time they touched a Select. A
 * cleared axis is deleted rather than written empty, so the canonical URL of an
 * unfiltered roster is the bare one.
 */
export function studentFilterSearchParams(
  filters: StudentFilters,
  params: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const [field, key] of Object.entries(URL_KEYS) as Array<
    [keyof StudentFilters, string]
  >) {
    const value = filters[field]
    if (value === null) next.delete(key)
    else next.set(key, String(value))
  }
  return next
}

/**
 * Whether two filter sets narrow the roster the same way.
 *
 * Field by field rather than by identity: the objects are rebuilt on every parse
 * of the URL, so identity says nothing. `useStudentFilters` uses this to tell an
 * *external* URL change - a Back, a link to the bare route - from the echo of
 * its own write, and only adopts the former.
 */
export function sameStudentFilters(a: StudentFilters, b: StudentFilters): boolean {
  return (Object.keys(NO_STUDENT_FILTERS) as Array<keyof StudentFilters>).every(
    (key) => a[key] === b[key]
  )
}

function isPhotoConsentFilter(value: string): value is PhotoConsentFilter {
  return (PHOTO_CONSENT_FILTERS as readonly string[]).includes(value)
}

/**
 * An integer inside a range, or null. `Number()` rather than `parseInt`, which
 * reads "9abc" as 9 — a URL that says one thing and filters by another.
 */
function parseIntegerParam(raw: string | null, min: number, max: number): number | null {
  if (raw === null || raw === '') return null
  const value = Number(raw)
  if (!Number.isInteger(value) || value < min || value > max) return null
  return value
}

function parseEnumParam<T extends string>(
  raw: string | null,
  isMember: (value: string) => value is T
): T | null {
  if (raw === null || !isMember(raw)) return null
  return raw
}
